import type { Position, CalculatorResult, CalculationMethod } from '../types';

/**
 * Lot tracks total cost (not unit price) so that transaction fees can be
 * baked into the cost basis. For a fee-less buy of qty Q at price P, total
 * cost is `Q * P`. With a fee F, total cost becomes `Q * P + F`. When a lot
 * is partially consumed, we pro-rate the cost by the consumed quantity:
 * `usedCost = (used / lot.qty) * lot.cost`.
 */
interface Lot {
  qty: number;
  cost: number; // total cost basis for the remaining qty in this lot
  id: string;
}

/**
 * Buy-side total cost — qty * price plus the optional transaction fee.
 * The fee field is undefined on legacy positions; treating it as 0 keeps
 * historical P&L numbers unchanged while letting new positions account for
 * exchange/gas/bridge fees correctly.
 */
function buyCost(p: Position): number {
  return p.quantity * p.price + (p.fee || 0);
}

/**
 * Sell-side proceeds for the actually-consumed quantity.
 * When the user tries to sell more than is available, we cap the realized
 * proceeds (and pro-rate the fee) to what was really sold. Avoids the
 * "phantom proceeds" bug where requesting to sell 5 BTC out of a 2 BTC
 * position would inflate realized P&L by the missing 3 BTC.
 */
function sellProceeds(p: Position, actualQty: number): number {
  if (p.quantity <= 0) return 0;
  const proRataFee = (actualQty / p.quantity) * (p.fee || 0);
  return actualQty * p.price - proRataFee;
}

/**
 * FIFO Calculator (First In, First Out)
 * Calcule les plus/moins-values selon la méthode FIFO.
 * Includes transaction fees in cost basis when `Position.fee` is present.
 */
export class FIFOCalculator {
  calculate(positions: Position[]): CalculatorResult {
    const lots: Lot[] = [];
    let realized = 0;
    const sorted = [...positions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    for (const p of sorted) {
      if (p.type === 'buy') {
        lots.push({ qty: p.quantity, cost: buyCost(p), id: p.id });
      } else {
        let sellQty = p.quantity;
        let usedCostTotal = 0;
        let actuallySold = 0;
        while (sellQty > 0 && lots.length) {
          const lot = lots[0];
          const used = Math.min(lot.qty, sellQty);
          const usedCost = lot.qty > 0 ? (used / lot.qty) * lot.cost : 0;
          usedCostTotal += usedCost;
          actuallySold += used;
          lot.cost -= usedCost;
          lot.qty -= used;
          sellQty -= used;
          if (lot.qty === 0) lots.shift();
        }
        realized += sellProceeds(p, actuallySold) - usedCostTotal;
      }
    }

    const remainingQty = lots.reduce((s, l) => s + l.qty, 0);
    const remainingCost = lots.reduce((s, l) => s + l.cost, 0);

    return {
      realizedPL: realized,
      remainingQty,
      totalCost: remainingCost,
      avgCost: remainingQty ? remainingCost / remainingQty : 0,
      method: 'FIFO'
    };
  }
}

/**
 * LIFO Calculator (Last In, First Out)
 * Identical fee handling to FIFO — only the lot consumption order differs.
 */
class LIFOCalculator {
  calculate(positions: Position[]): CalculatorResult {
    const lots: Lot[] = [];
    let realized = 0;
    const sorted = [...positions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    for (const p of sorted) {
      if (p.type === 'buy') {
        lots.push({ qty: p.quantity, cost: buyCost(p), id: p.id });
      } else {
        let sellQty = p.quantity;
        let usedCostTotal = 0;
        let actuallySold = 0;
        while (sellQty > 0 && lots.length) {
          const lot = lots[lots.length - 1]; // Dernier lot acheté (LIFO)
          const used = Math.min(lot.qty, sellQty);
          const usedCost = lot.qty > 0 ? (used / lot.qty) * lot.cost : 0;
          usedCostTotal += usedCost;
          actuallySold += used;
          lot.cost -= usedCost;
          lot.qty -= used;
          sellQty -= used;
          if (lot.qty === 0) lots.pop();
        }
        realized += sellProceeds(p, actuallySold) - usedCostTotal;
      }
    }

    const remainingQty = lots.reduce((s, l) => s + l.qty, 0);
    const remainingCost = lots.reduce((s, l) => s + l.cost, 0);

    return {
      realizedPL: realized,
      remainingQty,
      totalCost: remainingCost,
      avgCost: remainingQty ? remainingCost / remainingQty : 0,
      method: 'LIFO'
    };
  }
}

/**
 * Average Cost Calculator (Coût Moyen Pondéré)
 * Buy adds `qty * price + fee` to total cost. Sell consumes pro-rata cost
 * at the running average and subtracts the sell fee from realized.
 */
class AverageCostCalculator {
  calculate(positions: Position[]): CalculatorResult {
    let totalQty = 0;
    let totalCost = 0;
    let realized = 0;
    const sorted = [...positions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    for (const p of sorted) {
      if (p.type === 'buy') {
        totalQty += p.quantity;
        totalCost += buyCost(p);
      } else {
        const avgCost = totalQty > 0 ? totalCost / totalQty : 0;
        const sellQty = Math.min(p.quantity, totalQty);
        // Proceeds and fee both pro-rated to the actually-sold quantity,
        // matching the FIFO/LIFO behavior when the user requests more than
        // available.
        realized += sellProceeds(p, sellQty) - avgCost * sellQty;
        totalCost -= avgCost * sellQty;
        totalQty -= sellQty;
      }
    }

    return {
      realizedPL: realized,
      remainingQty: totalQty,
      totalCost: totalCost,
      avgCost: totalQty > 0 ? totalCost / totalQty : 0,
      method: 'AVERAGE_COST'
    };
  }
}

/**
 * Factory pour sélectionner le calculateur approprié
 */
export const CalculatorFactory = {
  getCalculator(method: CalculationMethod = 'FIFO'): FIFOCalculator | LIFOCalculator | AverageCostCalculator {
    switch (method.toUpperCase() as CalculationMethod) {
      case 'LIFO':
        return new LIFOCalculator();
      case 'AVERAGE_COST':
        return new AverageCostCalculator();
      case 'FIFO':
      default:
        return new FIFOCalculator();
    }
  },

  /**
   * Calcule directement selon la méthode choisie
   */
  calculate(positions: Position[], method: CalculationMethod = 'FIFO'): CalculatorResult {
    const calculator = this.getCalculator(method);
    return calculator.calculate(positions);
  }
};

/**
 * Calcule la quantité disponible (actuelle) d'un token
 * @param positions - Liste des positions (buy/sell)
 * @param excludePosId - ID de position à exclure (optionnel)
 * @returns Quantité disponible
 */
export function computeAvailableQty(positions: Position[], excludePosId: string | null = null): number {
  let qty = 0;
  const sorted = [...positions]
    .filter(p => !excludePosId || p.id !== excludePosId)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  for (const p of sorted) {
    qty += p.type === 'buy' ? p.quantity : -p.quantity;
  }

  return Math.max(qty, 0);
}
